import Box from '@ahamove/web-components/lib/Box';
import Chip from '@ahamove/web-components/lib/Chip';
import Divider from '@ahamove/web-components/lib/Divider';
import Dock from '@ahamove/web-components/lib/Dock';
import List from '@ahamove/web-components/lib/List';
import ListItem from '@ahamove/web-components/lib/ListItem';
import ListItemAvatar from '@ahamove/web-components/lib/ListItemAvatar';
import ListItemButton from '@ahamove/web-components/lib/ListItemButton';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import ListSubheader from '@ahamove/web-components/lib/ListSubheader';
import Stack from '@ahamove/web-components/lib/Stack';
import styled from '@ahamove/web-components/lib/styled';
import Typography from '@ahamove/web-components/lib/Typography';
import cn from 'classnames';
import dayjs from 'dayjs';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import type { Contract } from '@/api/contract/types';
import { useGetServiceModelDetail } from '@/api/service/useGetServiceModelDetail';
import { useWebInAppStore } from '@/global-stores/useWebInAppStore';
import { DEFAULT_DATE_TIME_FORMAT, RECORDS_LIST_STATUS } from '@/lib/constants';
import useIsMobile from '@/shared-hooks/useIsMobile';
import DateTimeRangeCalculation from '../new/booking/dates/DateTimeRangeCalculation';
import EmptyRecord from './EmptyRecord';
import ListRecordSkeleton from './ListRecordSkeleton';
import { recordConfig } from './recordConfig';

type Props = {
  title: string;
  data?: Contract[];
  loading: boolean;
};

type ListRecordItemProps = {
  data: Contract;
};

type StatusChipProps = {
  status: string;
};

const StatusChip = styled(Chip)(({ status }: StatusChipProps) => () => ({
  variant: 'light',
  fontWeight: 700,
  fontSize: 12,
  lineHeight: '16px',
  color: status === RECORDS_LIST_STATUS.CANCELLED ? '#697077' : '#FCFCFD',
  backgroundColor:
    status === RECORDS_LIST_STATUS.WAITING_TO_PICKUP
      ? '#8BC5E5'
      : status === RECORDS_LIST_STATUS.RENTING
      ? '#FA8C16'
      : status === RECORDS_LIST_STATUS.CLOSED
      ? '#58C27D'
      : status === RECORDS_LIST_STATUS.PAYING
      ? '#3772FF'
      : status === RECORDS_LIST_STATUS.CANCELLED
      ? '#C1C7CD'
      : status === RECORDS_LIST_STATUS.WAITING_TO_RETURN
      ? '#F5222D'
      : 'black',
}));

function ListRecordItem({ data }: ListRecordItemProps) {
  const { query } = useRouter();
  const { t } = useTranslation(recordConfig.i18nNamespaces);
  const isMobile = useIsMobile();
  const { data: serviceData } = useGetServiceModelDetail(
    data && data.service_id ? data.service_id : '',
    null,
    {
      enabled: Boolean(data.service_id),
    }
  );

  const localizeStatus = (status: string) => {
    switch (status) {
      case RECORDS_LIST_STATUS.WAITING_TO_PICKUP:
        return t('record:list.status.awaiting_pickup');
      case RECORDS_LIST_STATUS.RENTING:
        return t('record:list.status.ongoing');
      case RECORDS_LIST_STATUS.WAITING_TO_RETURN:
        return t('record:list.status.waiting_to_return');
      case RECORDS_LIST_STATUS.PAYING:
        return t('record:list.status.awaiting_payment');
      case RECORDS_LIST_STATUS.CLOSED:
        return t('record:list.status.closed');
      case RECORDS_LIST_STATUS.CANCELLED:
        return t('record:list.status.cancelled');
      case RECORDS_LIST_STATUS.VERIFIED:
        return t('record:list.status.verified');
      case RECORDS_LIST_STATUS.WAITING_TO_VERIFY:
        return t('record:list.status.waiting_to_verify');
      default:
        return status;
    }
  };

  if (isMobile)
    return (
      <Stack
        data-test-id="listRecordMobile"
        component={Link}
        href={{ pathname: `/bookings/${data._id}`, query }}
        direction="column"
        sx={{
          borderRadius: 4,
          boxShadow: '0px 10px 24px -5px rgba(15, 15, 15, 0.08)',
          p: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: 220,
            flex: 'none',
            position: 'relative',
          }}
        >
          <Image
            src={
              (serviceData && serviceData.photos[0]) ||
              '/static/icons/record/EmptyRecord.svg'
            }
            layout="fill"
            height={220}
            objectFit="contain"
          />
        </Box>

        <Stack
          direction="column"
          justifyContent="space-between"
          sx={{
            p: 2.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between" gap={1}>
            <Stack direction="column" gap={0.5}>
              <Typography
                variant="h4"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: '1',
                }}
              >
                {serviceData?.vehicle_model?.name}
              </Typography>
              <Typography variant="body2" color="grey">
                {t('record:list.booking_id')}: #{data._id}
              </Typography>
            </Stack>
            <StatusChip
              status={data.status}
              label={localizeStatus(data.status)?.toUpperCase()}
              size="medium"
              sx={{
                borderRadius: 1,
                py: 1,
                height: 30,
                '.MuiChip-label': {
                  height: 14,
                },
              }}
            />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Dock
            direction="column"
            sx={{
              p: 0,
            }}
          >
            <Stack direction="column" gap={0.5}>
              <Typography variant="h5">
                {t('record:list.rental_time')}
              </Typography>
              <Typography
                variant="body1"
                sx={(theme) => ({
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: '2',
                  color: theme.palette.grey[600],
                })}
              >
                <DateTimeRangeCalculation
                  type={data?.service_type}
                  range={[
                    data.expected_rent_time?.start_time,
                    data.expected_rent_time?.end_time,
                  ]}
                />
                {' | '}
                {dayjs
                  .unix(data.expected_rent_time?.start_time)
                  .format(DEFAULT_DATE_TIME_FORMAT)}
                {' - '}
                {dayjs
                  .unix(data.expected_rent_time?.end_time)
                  .format(DEFAULT_DATE_TIME_FORMAT)}
              </Typography>
            </Stack>
            {data.dropoff_location && data.dropoff_location.address ? (
              <Stack direction="column" gap={0.5}>
                <Typography variant="h5">
                  {t('record:list.pickup_return')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: '2',
                    color: theme.palette.grey[600],
                  })}
                >
                  {data.dropoff_location && data.dropoff_location.address}
                </Typography>
              </Stack>
            ) : null}
          </Dock>
        </Stack>
      </Stack>
    );

  return (
    <ListItem
      className="hover:!border-neutral-15 hover:!shadow-sm"
      sx={{
        height: 168,
        maxHeight: 168,
        borderRadius: 6,
        p: 0,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'common.white',
        bgcolor: 'common.white',
        boxShadow: '0px 10px 24px -5px rgba(15, 15, 15, 0.08)',
        transition: 'all 0.25s ease-out',
        overflow: 'hidden',
      }}
    >
      <ListItemButton
        disableRipple
        component={Link}
        href={{ pathname: `/bookings/${data._id}`, query }}
        sx={(theme) => ({
          pr: `${theme.spacing(2)} !important`,
          '&:hover': { bgcolor: 'common.white' },
        })}
      >
        <ListItemAvatar
          sx={{
            width: 263,
            height: 168,
            borderRadius: 2,
          }}
        >
          <Image
            src={
              (serviceData && serviceData.photos[0]) ||
              '/static/icons/record/EmptyRecord.svg'
            }
            width={263}
            height={168}
            objectFit="contain"
            className="rounded-l-2xl"
          />
        </ListItemAvatar>
        <ListItemText
          sx={{
            px: 3,
            py: 2,
            my: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: 168,
          }}
          primary={
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Stack direction="column" gap={0.5}>
                <Typography variant="h2">
                  {serviceData?.vehicle_model?.name}
                </Typography>
                <Typography variant="body1" color="grey">
                  {t('record:list.booking_id')}: #{data._id}
                </Typography>
              </Stack>
              <StatusChip
                status={data.status}
                label={localizeStatus(data.status)?.toUpperCase()}
                size="medium"
                sx={{
                  borderRadius: 1,
                  py: 1,
                  height: 30,
                  '.MuiChip-label': {
                    height: 14,
                  },
                }}
              />
            </Stack>
          }
          secondary={
            <Dock
              direction="row"
              sx={{
                p: 0,
              }}
            >
              <Stack direction="column" gap={0.5}>
                <Typography variant="h5">
                  {t('record:list.rental_time')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: '2',
                    color: theme.palette.grey[600],
                  })}
                >
                  <DateTimeRangeCalculation
                    type={data?.service_type}
                    range={[
                      data.expected_rent_time?.start_time,
                      data.expected_rent_time?.end_time,
                    ]}
                  />
                  {' | '}
                  {dayjs
                    .unix(data.expected_rent_time?.start_time)
                    .format(DEFAULT_DATE_TIME_FORMAT)}
                  {' - '}
                  {dayjs
                    .unix(data.expected_rent_time?.end_time)
                    .format(DEFAULT_DATE_TIME_FORMAT)}
                </Typography>
              </Stack>
              {data.dropoff_location && data.dropoff_location.address ? (
                <Stack direction="column" gap={0.5}>
                  <Typography variant="h5">
                    {t('record:list.pickup_return')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: '2',
                      color: theme.palette.grey[600],
                    })}
                  >
                    {data.dropoff_location && data.dropoff_location.address}
                  </Typography>
                </Stack>
              ) : null}
            </Dock>
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

export default function ListRecord({ title, data, loading }: Props) {
  const PAGE_SIZE = 22;
  const isMobile = useIsMobile();
  const { t } = useTranslation(recordConfig.i18nNamespaces);
  // const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  // const [fakeList, setFakeList] = useState<Contract[] | undefined>(undefined);
  // const [page, setPage] = useState<number>(0);
  // const [firstRender, setFirstRender] = useState<boolean>(false);
  const [isLoading] = useState<boolean>(false);
  const { isWebInApp } = useWebInAppStore();

  const fetchNextPage = () => {
    // if (data) {
    //   const nextData = data.slice(
    //     page * PAGE_SIZE,
    //     page * PAGE_SIZE + PAGE_SIZE
    //   );
    //   if (nextData && nextData.length) {
    //     setIsLoading(true);
    //   }
    //   const timeOut = setTimeout(() => {
    //     nextData.map((data) => {
    //       setFakeList((item) => (item ? [...item, data] : undefined));
    //     });
    //     setIsLoading(false);
    //   }, 1000);
    //   return () => clearTimeout(timeOut);
    // }
  };

  // useEffect(() => {
  //   if (data && !firstRender) {
  //     setFakeList(data.slice(0, 9));
  //     setFirstRender(true);
  //   }
  // }, [data, firstRender]);

  // useEffect(() => {
  //   if (fakeList && data && fakeList.length < data.length) {
  //     setHasNextPage(true);
  //     setPage((page) => page + 1);
  //   } else {
  //     setHasNextPage(false);
  //   }
  // }, [data, fakeList]);

  return (
    <List sx={{ width: '100%', bgcolor: 'common.white' }}>
      <ListSubheader
        className={cn(
          'desktop:!top-header-desktop !bg-white/80 backdrop-blur-sm backdrop-saturate-150',
          {
            '!top-header-mobile': !isWebInApp,
            '!top-12': isWebInApp,
          }
        )}
        sx={(theme) => ({
          ...(isMobile ? theme.typography.h2 : theme.typography.h1),
          color: 'grey.900',
          px: 2,
          py: isMobile ? 2 : 3,
          mx: isMobile ? 0 : -2,
          '&:after': {
            content: '""',
            height: '1px',
            bgcolor: 'grey.100',
            position: 'absolute',
            left: isMobile ? theme.spacing(2) : theme.spacing(2),
            right: isMobile ? theme.spacing(2) : theme.spacing(2),
            bottom: 0,
          },
        })}
      >
        {t(`record:list.${title}`)}
      </ListSubheader>

      <InfiniteScroll
        dataLength={data ? data.length : PAGE_SIZE}
        next={() => fetchNextPage()}
        hasMore={true}
        loader={isLoading ? <ListRecordSkeleton /> : null}
        className="desktop:px-6 desktop:-mx-6 space-y-4 px-4 pt-4 pb-8"
      >
        {data && data.length ? (
          data.map((item) => {
            return <ListRecordItem key={item._id} data={item} />;
          })
        ) : loading ? (
          <ListRecordSkeleton />
        ) : (
          <EmptyRecord title={t(`record:list.empty_${title}`) || undefined} />
        )}
      </InfiniteScroll>
    </List>
  );
}
